import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faSignOutAlt, 
  faUsers, 
  faQuestionCircle, 
  faInfoCircle, 
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import cidadania4uLogo from '../assets/cidadania4u-logo.svg';
import { HelpModal } from './HelpModal';
import { InfoModal } from './InfoModal';
import { logger } from '../utils/logger';

export const Header: React.FC = () => {
  const { user, logout, canManageUsers } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown quando clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    // Adicionar listener apenas quando o dropdown estiver aberto
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup do listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    try {
      // Fazer logout da aplicação
      await logout();
      
      // Redirecionar para login
      navigate('/login');
    } catch (error) {
      logger.error('Erro no logout:', error);
      // Mesmo com erro, redirecionar para login
      navigate('/login');
    }
  };

  const handleProfile = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleAdmin = () => {
    navigate('/admin');
    setShowDropdown(false);
  };

  const handleClientes = () => {
    navigate('/clientes');
    setShowDropdown(false);
  };

  const handleHelp = () => {
    setShowHelpModal(true);
  };

  const handleInfo = () => {
    setShowInfoModal(true);
  };

  const getRoleDisplay = (role: string) => {
    const roles = {
      master: 'Master',
      admin: 'Administrador',
      user: 'Usuário'
    };
    return roles[role as keyof typeof roles] || role;
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Seção da Marca */}
        <div className="header-brand">
          <div className="brand-logo">
            <img 
              src={cidadania4uLogo} 
              alt="Cidadania4u Logo" 
              className="company-logo"
            />
          </div>
          <div className="brand-content">
            <h1 className="brand-title">
              <span className="title-text">Checklist Espanha</span>
            </h1>
            <p className="brand-subtitle">
              Sistema de gerenciamento de documentos para cidadania espanhola
            </p>
          </div>
        </div>

        {/* Seção de Ações e Usuário */}
        {user && (
          <div className="header-actions">
            {/* Barra de Ferramentas */}
            <div className="toolbar">
              <button 
                onClick={handleHelp}
                className="toolbar-btn"
                title="Ajuda"
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </button>
              
              <button 
                onClick={handleInfo}
                className="toolbar-btn"
                title="Informações"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </button>
              
            </div>

            {/* Seção do Usuário */}
            <div className="user-section">
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-role">{getRoleDisplay(user.role)}</span>
              </div>
              
              <div className="user-menu" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="user-menu-btn"
                  title="Menu do usuário"
                >
                  <FontAwesomeIcon icon={faUser} />
                  <FontAwesomeIcon icon={faChevronDown} className="menu-arrow" />
                </button>
                
                {showDropdown && (
                  <div className="user-dropdown">
                    <button onClick={handleProfile} className="dropdown-item">
                      <FontAwesomeIcon icon={faUser} />
                      <span>Meu Perfil</span>
                    </button>
                    
                    <button onClick={handleClientes} className="dropdown-item">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>Clientes</span>
                    </button>
                    
                    {canManageUsers() && (
                      <button onClick={handleAdmin} className="dropdown-item">
                        <FontAwesomeIcon icon={faUsers} />
                        <span>Administração</span>
                      </button>
                    )}
                    
                    <button onClick={handleLogout} className="dropdown-item logout">
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Sair</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modais */}
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
      <InfoModal 
        isOpen={showInfoModal} 
        onClose={() => setShowInfoModal(false)} 
      />
    </header>
  );
};